"use client"

import { useState, useEffect } from "react"
import { useRouter } from "next/navigation"
import {
  IconLogout,
  IconRefresh,
  IconCheck,
  IconUserPlus,
  IconDotsVertical,
  IconHelp,
  IconX,
  IconSparkles,
  IconHistory,
  IconMap2,
  IconBug,
  IconMessageCircle,
  IconRobot,
  IconMessageReport,
} from "@tabler/icons-react"

import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@/components/ui/avatar"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar"
import { useUser } from "@/components/user-context"
import { trackAnalyticsEvent } from "@/lib/api-v3"
import { DEV_MODE_STORAGE_KEY } from "@/lib/auth"
import { toast } from "sonner"
import { AccountLoginDialog } from "@/components/account-login-dialog"
import { LogoutConfirmDialog } from "@/components/logout-confirm-dialog"
import { useFeedback } from "@/components/feedback/feedback-provider"

export function NavUser({
  user,
}: {
  user: {
    name: string
    email: string
    avatar: string
  }
}) {
  const { isMobile } = useSidebar()
  const router = useRouter()
  const feedback = useFeedback()
  const { 
    isRefreshing, 
    accounts, 
    activeAccountId, 
    switchAccount, 
    removeAccount, 
    removeAllAccounts,
    userWorkspace,
  } = useUser()
  const [isLoginDialogOpen, setIsLoginDialogOpen] = useState(false)
  const [logoutTarget, setLogoutTarget] = useState<{ id: string; name: string; login: string } | null>(null)
  const [isLogoutAllOpen, setIsLogoutAllOpen] = useState(false)
  const [devMode, setDevMode] = useState(false)

  useEffect(() => {
    setDevMode(localStorage.getItem(DEV_MODE_STORAGE_KEY) === 'true')
    const handler = () => setDevMode(localStorage.getItem(DEV_MODE_STORAGE_KEY) === 'true')
    window.addEventListener('storage', handler)
    return () => window.removeEventListener('storage', handler)
  }, [])

  const handleSwitchAccount = (accountId: string) => {
    try {
      if (accountId === activeAccountId) return
      toast.info("Переключение аккаунта...")
      switchAccount(accountId)
    } catch (error) {
      console.error("Ошибка при переключении аккаунта:", error)
      toast.error("Ошибка при переключении аккаунта")
    }
  }

  const handleLogoutClick = (account: { id: string; name: string; login: string }, e: React.MouseEvent) => {
    e.stopPropagation()
    setLogoutTarget(account)
  }

  const handleLogoutConfirm = async () => {
    if (!logoutTarget) return
    try {
      // 📊 Аналитика: ручной выход — await чтобы событие ушло ДО удаления токена
      const account = accounts.find(a => a.id === logoutTarget.id)
      if (account) {
        await trackAnalyticsEvent(account.tokens.accessToken, {
          name: 'logout',
          type: 0,
          value: null,
          timeZone: Math.round(-new Date().getTimezoneOffset() / 60),
          workspaceId: userWorkspace?.workspaceId ?? null,
        })
      }
      toast.info(`Выход из ${logoutTarget.name}...`)
      removeAccount(logoutTarget.id)
    } catch (error) {
      console.error("Ошибка при выходе:", error)
      toast.error("Ошибка при выходе из аккаунта")
    } finally {
      setLogoutTarget(null)
    }
  }

  const handleLogoutAll = async () => {
    try {
      // 📊 Аналитика: выход из всех — await чтобы событие ушло ДО удаления токенов
      const activeAccount = accounts.find(a => a.id === activeAccountId)
      if (activeAccount) {
        await trackAnalyticsEvent(activeAccount.tokens.accessToken, {
          name: 'logout',
          type: 0,
          value: null,
          timeZone: Math.round(-new Date().getTimezoneOffset() / 60),
          workspaceId: userWorkspace?.workspaceId ?? null,
        })
      }
      toast.info("Выход из всех аккаунтов...")
      removeAllAccounts()
    } catch (error) {
      console.error("Ошибка при выходе:", error)
      toast.error("Ошибка при выходе из всех аккаунтов")
    } finally {
      setIsLogoutAllOpen(false)
    }
  }

  return (
    <>
      <SidebarMenu>
        <SidebarMenuItem>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <SidebarMenuButton
                size="lg"
                className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
              >
                <Avatar className={isMobile ? "h-10 w-10 rounded-lg grayscale" : "h-8 w-8 rounded-lg grayscale"}>
                  <AvatarImage src={user.avatar} alt={user.name} />
                  <AvatarFallback className="rounded-lg">{user.name.slice(0, 2).toUpperCase()}</AvatarFallback>
                </Avatar>
                <div className={`grid flex-1 text-left leading-tight ${isMobile ? 'text-base' : 'text-sm'}`}>
                  <span className={`truncate font-medium ${isMobile ? 'text-base' : ''}`}>
                    {user.name}
                  </span>
                  <span className={`text-muted-foreground truncate ${isMobile ? 'text-sm' : 'text-xs'}`}>
                    {user.email}
                  </span>
                </div>
                <div className="ml-auto flex items-center gap-1">
                  {isRefreshing && (
                    <IconRefresh className={`animate-spin text-blue-500 ${isMobile ? 'size-4' : 'size-3'}`} />
                  )}
                  <IconDotsVertical className="size-4" />
                </div>
              </SidebarMenuButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className={`w-(--radix-dropdown-menu-trigger-width) rounded-lg ${isMobile ? 'min-w-72' : 'min-w-56'}`}
              side={isMobile ? "bottom" : "right"}
              align="end"
              sideOffset={4}
            >
              {/* Список всех аккаунтов */}
              {accounts.length > 0 && (
                <>
                  <DropdownMenuGroup>
                    {accounts.map((account) => {
                      const isActive = account.id === activeAccountId

                      return (
                        <DropdownMenuItem
                          key={account.id}
                          onClick={() => handleSwitchAccount(account.id)}
                          className="cursor-pointer flex items-center gap-2"
                        >
                          <Avatar className="h-7 w-7 rounded-md flex-shrink-0">
                            <AvatarImage src="/avatars/shadcn.jpg" alt={account.user.name} />
                            <AvatarFallback className="rounded-md text-xs">
                              {account.user.name.slice(0, 2).toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1 text-left leading-tight min-w-0">
                            <div className="font-medium text-sm truncate">{account.user.name}</div>
                            <div className="text-xs text-muted-foreground truncate">{account.user.login}</div>
                          </div>

                          <div className="flex-shrink-0 flex items-center gap-0.5">
                            {isActive && <IconCheck className="size-4" />}
                            <button
                              className="p-1 rounded-sm cursor-pointer text-muted-foreground hover:text-red-500 transition-colors"
                              onClick={(e) => handleLogoutClick({ id: account.id, name: account.user.name, login: account.user.login }, e)}
                              title="Удалить аккаунт"
                            >
                              <IconX className="size-3.5 text-current" />
                            </button>
                          </div>
                        </DropdownMenuItem>
                      )
                    })}
                  </DropdownMenuGroup>
                  <DropdownMenuSeparator />
                </>
              )}

              {/* Кнопка "Войти в новый аккаунт" */}
              <DropdownMenuItem
                onClick={() => setIsLoginDialogOpen(true)}
                className={`cursor-pointer ${isMobile ? 'text-base py-2 px-2' : ''}`}
              >
                <IconUserPlus className="size-4" />
                Войти ещё в аккаунт
              </DropdownMenuItem>

              <DropdownMenuItem
                onClick={() => router.push("/messenger")}
                className={`cursor-pointer ${isMobile ? 'text-base py-2 px-2' : ''}`}
              >
                <IconMessageCircle className="size-4" />
                Мессенджер
                <span className="ml-auto text-[10px] font-medium text-[#3390ec]">бета</span>
              </DropdownMenuItem>

              {feedback && (
                <DropdownMenuItem
                  // форма открывается после закрытия меню, иначе Radix вернёт фокус в меню
                  onClick={() => setTimeout(feedback.openProduct, 0)}
                  className={`cursor-pointer ${isMobile ? 'text-base py-2 px-2' : ''}`}
                  data-testid="nav-feedback"
                >
                  <IconMessageReport className="size-4" />
                  Обратная связь
                </DropdownMenuItem>
              )}

              {/* Справка → подменю */}
              <DropdownMenuSub>
                <DropdownMenuSubTrigger
                  className={`cursor-pointer gap-2 [&_svg:not([class*='text-'])]:text-muted-foreground [&_svg:not([class*='size-'])]:size-4 ${isMobile ? 'text-base py-2 px-2' : ''}`}
                >
                  <IconHelp className="size-4" />
                  Справка
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent>
                  <DropdownMenuItem
                    onClick={() => window.open("/updates", "_blank")}
                    className="cursor-pointer"
                  >
                    <IconSparkles className="size-4" />
                    Что нового
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => window.open("/help/copilot", "_blank")}
                    className="cursor-pointer"
                  >
                    <IconRobot className="size-4" />
                    Возможности копилота
                  </DropdownMenuItem>
                  {devMode && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() => window.open("/changelogs/current", "_blank")}
                        className="cursor-pointer"
                      >
                        <IconHistory className="size-4" />
                        <span>История версий</span>
                        <span className="ml-auto text-[10px] font-medium text-amber-500">dev</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => window.open("/roadmap", "_blank")}
                        className="cursor-pointer"
                      >
                        <IconMap2 className="size-4" />
                        <span>Дорожная карта</span>
                        <span className="ml-auto text-[10px] font-medium text-amber-500">dev</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => window.open("/bugs", "_blank")}
                        className="cursor-pointer"
                      >
                        <IconBug className="size-4" />
                        <span>Баг-трекер</span>
                        <span className="ml-auto text-[10px] font-medium text-amber-500">dev</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => window.open("/development/bugfix-agent", "_blank")}
                        className="cursor-pointer"
                      >
                        <IconRobot className="size-4" />
                        <span>Разработка</span>
                        <span className="ml-auto text-[10px] font-medium text-amber-500">dev</span>
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuSubContent>
              </DropdownMenuSub>

              <DropdownMenuSeparator />

              {/* Кнопка "Выйти из всех аккаунтов" */}
              <DropdownMenuItem
                onClick={() => setIsLogoutAllOpen(true)}
                className={`text-red-600 focus:text-red-600 cursor-pointer ${isMobile ? 'text-base py-2 px-2' : ''}`}
              >
                <IconLogout className="size-4 text-current" />
                Выйти из всех аккаунтов
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </SidebarMenuItem>
      </SidebarMenu>

      <AccountLoginDialog
        open={isLoginDialogOpen}
        onOpenChange={setIsLoginDialogOpen}
      />

      <LogoutConfirmDialog
        open={logoutTarget !== null}
        onOpenChange={(open) => { if (!open) setLogoutTarget(null) }}
        accountName={logoutTarget?.name ?? ""}
        accountLogin={logoutTarget?.login ?? ""}
        onConfirm={handleLogoutConfirm}
      />

      <LogoutConfirmDialog
        open={isLogoutAllOpen}
        onOpenChange={setIsLogoutAllOpen}
        accountName=""
        accountLogin=""
        dialogTitle="Выйти из всех аккаунтов?"
        dialogDescription="Вы выйдете из всех сохранённых аккаунтов. Для повторного входа потребуется ввести пароль."
        confirmLabel="Выйти из всех"
        onConfirm={handleLogoutAll}
      />
    </>
  )
}
